import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { UserService, UserInformation } from '../../services/user.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { environment } from '../../../environments/environment';

export interface ExamListDetail {
  id: string;
  hoursStudied: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  enrolledAt: string;
  completedAt: string | null;
  progress: string;
  isCompleted: boolean;
  examResult?: boolean | null;   // null = ยังไม่ประกาศผล, true = ผ่าน, false = ไม่ผ่าน
  createdAt: string;
  updatedAt: string;
  userId: string;
  examId: string;
  examListStatusId: number;
  examListPaymentStatusId: number;
  paymentSlip: string | null;
  amount: string | null;
  originalSlipStatus: boolean;
  attachFile: string | null;
  transferDateTime?: string | null;
  user: {
    id: string;
    email: string;
    password: string;
    cid: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    companyId: string | null;
  };
  exam: {
    id: string;
    examTitle: string;
    valuerRank: string;
    registrationDeadline: string;
    examDate: string;
    examTime: string;
    examLocation: string;
    examFee: string;
    remarks: string;
    posterFile: string | null;
    certificateFile: string | null;
    createdAt: string;
    updatedAt: string;
    examStatusId: number;
  };
  examListStatus?: {
    id: number;
    name: string;
  };
  examListPaymentStatus: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  examConfirmStatus?: {
    id: number;
    name: string;
  };
}

@Component({
  selector: 'app-exam-list-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbAlertModule,
    StaffHeaderComponent,
    StaffNavComponent
  ],
  templateUrl: './exam-list-detail.component.html',
  styleUrl: './exam-list-detail.component.scss'
})
export class ExamListDetailComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;

  examListId: string | null = null;
  examDetail: ExamListDetail | null = null;
  applicantInfo: UserInformation | null = null;
  isLoading = false;
  error: string | null = null;
  successMessage: string | null = null;
  slipModalUrl: string | null = null;

  confirmationStatusOptions = [
    { id: 1, name: 'รอ Approve' },
    { id: 2, name: 'ยกเลิกใบคำขอ' },
    { id: 3, name: 'Approved' },
    { id: 4, name: 'คำขอเก่า' }
  ];

  verificationOptions = [
    { value: false, label: 'รอยืนยัน' },
    { value: true, label: 'ยืนยันแล้ว' }
  ];

  // ผลการสอบ 3 สถานะ เพื่อแยก "ยังไม่ประกาศผล" ออกจาก "ไม่ผ่าน"
  completionOptions: Array<{ value: boolean | null; label: string }> = [
    { value: null, label: 'ยังไม่ประกาศผล' },
    { value: true, label: 'ผ่าน' },
    { value: false, label: 'ไม่ผ่าน' }
  ];

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('🔄 ExamListDetailComponent ngOnInit');

    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    this.route.paramMap.subscribe(params => {
      this.examListId = params.get('id');
      console.log('📋 Exam List ID from route:', this.examListId);

      if (this.examListId) {
        this.loadExamDetail();
      } else {
        this.error = 'ไม่พบรหัสรายการสอบ';
      }
    });
  }

  ngOnDestroy(): void {
    console.log('🗑️ ExamListDetailComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  loadExamDetail(): void {
    if (!this.examListId) return;

    console.log('🚀 Loading exam list detail from API...');
    this.error = null;

    const apiUrl = `${environment.apiUrl}/exam-lists/${this.examListId}`;
    console.log('📡 Fetching from:', apiUrl);

    this.http.get<ExamListDetail>(apiUrl)
      .subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          console.log('✅ Response status: Success (200 or 304)');
          this.examDetail = response;

          // โหลดข้อมูลผู้สมัคร (ชื่อ เบอร์โทร หน่วยงาน) เพิ่มเติม เหมือนใบคำขอสมัครคอร์สอบรม
          const applicantId = response.userId || response.user?.id;
          if (applicantId) {
            this.loadApplicantInfo(applicantId);
          }

          this.cdr.detectChanges();
          console.log('🔄 Change detection triggered - content should display now');
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);

          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`;
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * โหลดข้อมูลผู้สมัคร (ชื่อ-สกุล เบอร์โทร หน่วยงาน) จาก full-details
   */
  loadApplicantInfo(userId: string): void {
    this.userService.getUserById(userId).subscribe({
      next: (user) => {
        this.applicantInfo = user?.userInformation || null;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.warn('⚠️ ไม่สามารถโหลดข้อมูลผู้สมัครเพิ่มเติมได้:', error);
      }
    });
  }

  /**
   * ชื่อ-สกุลผู้สมัคร (ไทยก่อน ตกไปอังกฤษ/legacy)
   */
  getApplicantName(): string {
    const info = this.applicantInfo;
    if (!info) return '-';
    const first = info.firstNameThai || info.firstNameEnglish || info.firstName || '';
    const last = info.lastNameThai || info.lastNameEnglish || info.lastName || '';
    const name = `${first} ${last}`.trim();
    return name || '-';
  }

  /**
   * เบอร์โทรผู้สมัคร
   */
  getApplicantPhone(): string {
    const info = this.applicantInfo;
    if (!info) return '-';
    return info.phoneNumber
      || info.currentPhone
      || info.company?.mobilePhone
      || info.company?.officePhone
      || '-';
  }

  /**
   * หน่วยงาน/สังกัดผู้สมัคร
   */
  getApplicantOrganization(): string {
    const info = this.applicantInfo;
    if (!info) return '-';
    return info.company?.organizationName
      || info.workplaceName
      || info.organization
      || '-';
  }

  goBack(): void {
    this.router.navigate(['/staff/exam-list-management']);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatPrice(price: string): string {
    const numPrice = parseFloat(price);
    return numPrice.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  getLevelText(level: string): string {
    switch (level) {
      case 'beginner':
        return 'เริ่มต้น';
      case 'intermediate':
        return 'กลาง';
      case 'advanced':
        return 'สูง';
      default:
        return level;
    }
  }

  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'beginner':
        return 'bg-info';
      case 'intermediate':
        return 'bg-primary';
      case 'advanced':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getExamListStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ยืนยันแล้ว':
      case 'อนุมัติ':
      case 'Approved':
        return 'bg-success';
      case 'รอยืนยัน':
      case 'รอการอนุมัติ':
      case 'รอ Approve':
        return 'bg-warning';
      case 'ไม่อนุมัติ':
      case 'ยกเลิก':
      case 'ยกเลิกใบคำขอ':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getConfirmationStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'Approved':
        return 'bg-success';
      case 'รอ Approve':
        return 'bg-warning';
      case 'ยกเลิกใบคำขอ':
        return 'bg-danger';
      case 'คำขอเก่า':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  getConfirmationStatusName(statusId: number): string {
    const status = this.confirmationStatusOptions.find(s => s.id === statusId);
    return status ? status.name : 'ไม่ระบุ';
  }

  getExamPaymentStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ชำระเงินเสร็จสิ้น':
      case 'ชำระเงินแล้ว':
      case 'ชำระแล้ว':
        return 'bg-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบยอดชำระ':
      case 'รอตรวจสอบ':
        return 'bg-info';
      case 'ยกเลิกการชำระ':
      case 'ยกเลิก':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getCompletionBadgeClass(examResult: boolean | null | undefined): string {
    if (examResult === true) return 'bg-success';
    if (examResult === false) return 'bg-danger';
    return 'bg-secondary';
  }

  getCompletionText(examResult: boolean | null | undefined): string {
    if (examResult === true) return 'ผ่าน';
    if (examResult === false) return 'ไม่ผ่าน';
    return 'ยังไม่ประกาศผล';
  }

  getActiveStatusBadgeClass(isActive: boolean): string {
    return isActive ? 'bg-success' : 'bg-secondary';
  }

  getActiveStatusText(isActive: boolean): string {
    return isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน';
  }

  updateConfirmationStatus(newStatusId: number): void {
    if (!this.examDetail || !this.examListId) return;

    console.log('✅ Updating confirmation status to:', newStatusId);

    const apiUrl = `${environment.apiUrl}/exam-lists/${this.examListId}`;

    this.http.patch(apiUrl, { examListStatusId: newStatusId }).subscribe({
      next: (response: any) => {
        console.log('✅ Confirmation status updated:', response);
        this.successMessage = 'อัปเดตสถานะการยืนยันสำเร็จ';
        this.loadExamDetail();
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating confirmation status:', error);
        this.error = `เกิดข้อผิดพลาดในการอัปเดตสถานะการยืนยัน: ${error.message}`;
        this.cdr.detectChanges();
      }
    });
  }

  updateVerificationStatus(isVerified: boolean): void {
    if (!this.examDetail || !this.examListId) return;

    console.log('✅ Updating verification status to:', isVerified);

    const apiUrl = `${environment.apiUrl}/exam-lists/${this.examListId}`;

    this.http.patch(apiUrl, { isVerified }).subscribe({
      next: (response: any) => {
        console.log('✅ Verification status updated:', response);
        this.successMessage = 'อัปเดตสถานะการยืนยันสำเร็จ';
        this.loadExamDetail();
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating verification status:', error);
        this.error = `เกิดข้อผิดพลาดในการอัปเดตสถานะการยืนยัน: ${error.message}`;
        this.cdr.detectChanges();
      }
    });
  }

  updateCompletionStatus(examResult: boolean | null): void {
    if (!this.examDetail || !this.examListId) return;

    console.log('🎓 Updating exam result to:', examResult);

    const apiUrl = `${environment.apiUrl}/exam-lists/${this.examListId}`;

    this.http.patch(apiUrl, { examResult }).subscribe({
      next: (response: any) => {
        console.log('✅ Completion status updated:', response);
        this.successMessage = 'อัปเดตสถานะการสอบจบสำเร็จ';
        this.loadExamDetail();
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating completion status:', error);
        this.error = `เกิดข้อผิดพลาดในการอัปเดตสถานะการสอบจบ: ${error.message}`;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Check if file is an image based on URL extension
   */
  isImageFile(fileUrl: string | null): boolean {
    if (!fileUrl) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const lowerUrl = fileUrl.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.endsWith(ext));
  }

  /**
   * Check if file is a PDF based on URL extension
   */
  isPdfFile(fileUrl: string | null): boolean {
    if (!fileUrl) return false;
    return fileUrl.toLowerCase().endsWith('.pdf');
  }
}
